import {
  Home as HomeIcon, Info, Phone, BookOpen, FileText, HelpCircle, Images, MessageSquareQuote,
} from 'lucide-react';

// ─── Page-content schema: 'default' (Classic) and 'theme-2' (Editorial) ───
//
// Both presets render their page bodies from the same content keys, so one
// schema serves both. They do not render every key, though: a field or a
// section that only one of them draws carries `layouts`, and
// `resolveSchemaForLayout` (./index.js) drops it for the other — a field that
// changes nothing on the admin's site is worse than no field.
//
// Every key here is read by vc_cms. Sections:
//   id          — stable handle (the setup checklist links to it)
//   visibility  — { key, layouts } — a show/hide toggle in the section header.
//                 Absent or true means shown: vc_cms only hides on `false`.
//                 `optIn: true` reverses that — absent means HIDDEN — for a
//                 section added after live sites were built (the home page's
//                 Trainers & mentors); `hiddenNote` is its notice while off.
//   layouts     — ['default'] | ['theme-2'] — omit when both render it
//   visibleWhen — { key, equals } | { key, notEquals } on a section or a
//                 field: shown only while another key on the page holds (or
//                 does not hold) that value. Hidden copy is kept, not cleared.
//
// The header, menu and footer are not pages; they are edited in the Header &
// Footer tab, which writes the `global` keys this schema used to carry.

const CLASSIC = ['default'];
const EDITORIAL = ['theme-2'];

// The About page's people: one section, or a Trainers and a Mentors section.
// `teamLayout` absent = one section, as every site had before the choice.
const ONE_TEAM_SECTION = { key: 'teamLayout', notEquals: 'separate' };
const TWO_TEAM_SECTIONS = { key: 'teamLayout', equals: 'separate' };

const BI_FEATURE_ICONS = [
  'bi-briefcase-fill', 'bi-code-square', 'bi-person-video3', 'bi-award-fill',
  'bi-rocket-takeoff', 'bi-star-fill', 'bi-lightning-fill', 'bi-shield-check',
  'bi-people-fill', 'bi-laptop', 'bi-book', 'bi-chat-dots', 'bi-graph-up',
  'bi-buildings', 'bi-currency-rupee', 'bi-trophy', 'bi-headset', 'bi-clock-history',
].map((v) => ({ value: v, label: v.replace('bi-', '') }));

const BI_STAT_ICONS = [
  'bi-people-fill', 'bi-briefcase-fill', 'bi-building', 'bi-star-fill',
  'bi-trophy', 'bi-graph-up', 'bi-currency-rupee', 'bi-award-fill',
].map((v) => ({ value: v, label: v.replace('bi-', '') }));

const BI_CTA_ICONS = [
  'bi-mortarboard', 'bi-people', 'bi-trophy', 'bi-briefcase-fill',
  'bi-star-fill', 'bi-rocket-takeoff', 'bi-award-fill', 'bi-graph-up',
].map((v) => ({ value: v, label: v.replace('bi-', '') }));

const faqRepeater = (key, { category = false, hint } = {}) => ({
  key,
  label: 'Questions',
  type: 'repeater',
  hint,
  itemLabel: 'Question',
  titleKey: 'question',
  addLabel: 'Add question',
  defaultItem: category ? { question: '', answer: '', category: '' } : { question: '', answer: '' },
  item: [
    { key: 'question', label: 'Question', type: 'text', wide: true },
    {
      key: 'answer',
      label: 'Answer',
      type: 'textarea',
      rows: 3,
      // The public FAQ accordion splits on blank lines.
      hint: 'Leave a blank line between paragraphs.',
    },
    ...(category
      ? [{ key: 'category', label: 'Group', type: 'text', placeholder: 'e.g. Fees — questions with the same group are shown together' }]
      : []),
  ],
});

const statsRepeater = (hint) => ({
  key: 'stats',
  label: 'Numbers',
  type: 'repeater',
  hint,
  itemLabel: 'Number',
  titleKey: 'label',
  addLabel: 'Add number',
  inline: true,
  max: 6,
  defaultItem: { icon: 'bi-people-fill', number: '', label: '' },
  item: [
    { key: 'icon', label: 'Icon', type: 'icon', options: BI_STAT_ICONS },
    { key: 'number', label: 'Figure', type: 'text', placeholder: 'e.g. 5,000+' },
    { key: 'label', label: 'Label', type: 'text', placeholder: 'e.g. Students trained' },
  ],
});

const banner = (titlePlaceholder, subtitlePlaceholder) => ({
  id: 'banner',
  title: 'Page banner',
  hint: 'The heading at the top of the page.',
  fields: [
    { key: 'bannerTitle', label: 'Title', type: 'text', placeholder: titlePlaceholder },
    { key: 'bannerSubtitle', label: 'Subtitle', type: 'textarea', rows: 2, placeholder: subtitlePlaceholder },
  ],
});

const DEFAULT_SCHEMA = [
  // ── Home ────────────────────────────────────────────────────────────────
  {
    pageKey: 'home',
    label: 'Home',
    icon: HomeIcon,
    path: '/',
    intro: 'Sections appear on the page in this order. Anything left blank shows the sample text shipped with the site.',
    sections: [
      {
        id: 'hero',
        title: 'Hero',
        hint: 'The first screen of your website — headline, buttons and the main photo.',
        fields: [
          { key: 'heroBadge', label: 'Badge above the headline', type: 'text', placeholder: 'Trusted by 5,000+ Students Across India' },
          { key: 'heroTitle', label: 'Headline', type: 'text', placeholder: 'Launch Your Tech Career with' },
          { key: 'heroHighlight', label: 'Highlighted words', type: 'text', placeholder: 'Expert-Led Training', hint: 'Shown after the headline, in your brand colour.' },
          { key: 'heroSubtitleEm', label: 'Closing line', type: 'text', placeholder: 'Get hired.', layouts: EDITORIAL },
          { key: 'heroSubtitle', label: 'Supporting text', type: 'textarea', rows: 3, placeholder: 'Industry-ready courses in Full Stack, Data Science, DevOps and more…' },
          { key: 'heroPrimaryCta', label: 'Main button', type: 'text', placeholder: 'Explore Courses', hint: 'Opens your courses page.' },
          { key: 'heroSecondaryCta', label: 'Second button', type: 'text', placeholder: 'Book Free Demo', hint: 'Opens the demo request form.' },
          {
            key: 'heroImage',
            label: 'Main photo',
            type: 'image',
            hint: 'Replaces the sample photo. Landscape, about 1200 × 1050 px.',
          },
          { key: 'heroImageAlt', label: 'Photo description', type: 'text', placeholder: 'e.g. Students in a classroom at Your Institute', hint: 'Read aloud by screen readers and used by search engines.' },
          { key: 'heroFloat1Title', label: 'Floating card 1 — title', type: 'text', placeholder: '92% Placement' },
          { key: 'heroFloat1Subtitle', label: 'Floating card 1 — text', type: 'text', placeholder: 'Industry-leading rate' },
          { key: 'heroFloat2Title', label: 'Floating card 2 — title', type: 'text', placeholder: 'Placed at MNCs' },
          { key: 'heroFloat2Subtitle', label: 'Floating card 2 — text', type: 'text', placeholder: 'TCS, Infosys, Wipro & more' },
          { key: 'heroTrustRating', label: 'Rating line', type: 'text', placeholder: '4.9/5 from 2,000+ reviews' },
          { key: 'heroTrustSuffix', label: 'Text after the rating', type: 'text', placeholder: '· live mentors · outcomes-first', layouts: EDITORIAL },
          {
            key: 'showTrustStrip',
            label: 'Show the rating strip',
            type: 'toggle',
            hint: 'The row of student photos and stars under the buttons.',
            layouts: CLASSIC,
          },
          {
            key: 'trustAvatars',
            label: 'Rating strip photos',
            type: 'repeater',
            hint: 'Up to five small, square photos of real students. Until you add one, sample stock photos are shown.',
            itemLabel: 'Photo',
            addLabel: 'Add photo',
            max: 5,
            defaultItem: { image: '' },
            coerceRow: (row) => (typeof row === 'string' ? { image: row } : row),
            item: [{ key: 'image', label: 'Photo', type: 'image', compact: true }],
            layouts: CLASSIC,
          },
        ],
      },
      {
        id: 'stats',
        title: 'Key numbers',
        hint: 'The row of figures under the hero.',
        visibility: { key: 'showStats', layouts: CLASSIC },
        fields: [
          statsRepeater('Until you add your own, sample figures such as “5,000+ Students Trained” are shown.'),
        ],
      },
      {
        id: 'partners',
        title: 'Placement partners',
        hint: 'The moving strip of company names or logos.',
        layouts: CLASSIC,
        visibility: { key: 'showPartners', layouts: CLASSIC },
        fields: [
          { key: 'partnersLabel', label: 'Strip label', type: 'text', placeholder: 'Our students work at top companies', layouts: CLASSIC },
          {
            key: 'partners',
            label: 'Companies',
            type: 'repeater',
            hint: 'Upload a wide logo, or leave the logo empty to show the name. Until you add one, sample company names are shown.',
            itemLabel: 'Company',
            titleKey: 'name',
            addLabel: 'Add company',
            defaultItem: { name: '', logo: '' },
            // Older saves stored plain strings.
            coerceRow: (row) => (typeof row === 'string' ? { name: row.trim(), logo: '' } : row),
            inline: true,
            item: [
              { key: 'name', label: 'Company name', type: 'text', placeholder: 'e.g. TCS' },
              { key: 'logo', label: 'Logo', type: 'image', compact: true },
            ],
          },
        ],
      },
      {
        id: 'features',
        title: 'Why choose us',
        hint: 'Your strengths, as a heading and a grid of cards.',
        visibility: { key: 'showFeatures', layouts: CLASSIC },
        fields: [
          { key: 'featuresBadge', label: 'Badge', type: 'text', placeholder: 'Why Choose Us' },
          { key: 'featuresTitle', label: 'Heading', type: 'text', placeholder: 'What Makes Us Different' },
          { key: 'featuresSubtitle', label: 'Supporting text', type: 'textarea', rows: 2 },
          { key: 'featuresQuote', label: 'Pull quote', type: 'textarea', rows: 2, layouts: EDITORIAL },
          {
            key: 'features',
            label: 'Cards',
            type: 'repeater',
            hint: 'Until you add your own, four sample cards are shown.',
            itemLabel: 'Card',
            titleKey: 'title',
            addLabel: 'Add card',
            inline: true,
            columns: 2,
            max: 8,
            defaultItem: { icon: 'bi-star-fill', title: '', text: '' },
            item: [
              { key: 'icon', label: 'Icon', type: 'icon', options: BI_FEATURE_ICONS },
              { key: 'title', label: 'Title', type: 'text', placeholder: 'e.g. Placement support' },
              { key: 'text', label: 'Description', type: 'textarea', rows: 2 },
            ],
          },
        ],
      },
      {
        id: 'courses',
        title: 'Popular courses',
        hint: 'A grid of course cards linking to each course page.',
        visibility: { key: 'showCourses', layouts: CLASSIC },
        fields: [
          {
            key: 'featuredCourses',
            label: 'Courses to feature',
            type: 'courses',
            max: 6,
            hint: 'Pick up to six, in the order they should appear. With none picked, your newest courses are shown.',
          },
          { key: 'coursesBadge', label: 'Badge', type: 'text', placeholder: 'Our Courses' },
          { key: 'coursesTitle', label: 'Heading', type: 'text', placeholder: 'Popular Courses' },
          { key: 'coursesSubtitle', label: 'Supporting text', type: 'textarea', rows: 2 },
          { key: 'coursesButtonText', label: 'Button', type: 'text', placeholder: 'View All Courses', layouts: CLASSIC },
        ],
      },
      {
        id: 'batches',
        title: 'Upcoming batches',
        hint: 'Batches starting soon, taken from your batch schedule.',
        layouts: CLASSIC,
        visibility: { key: 'showBatches', layouts: CLASSIC },
        fields: [
          { key: 'batchesBadge', label: 'Badge', type: 'text', placeholder: 'Upcoming Batches' },
          { key: 'batchesTitle', label: 'Heading', type: 'text', placeholder: 'Next Batches Starting Soon' },
          { key: 'batchesSubtitle', label: 'Supporting text', type: 'textarea', rows: 2 },
        ],
      },
      {
        // Between what is taught and what students say about it. The same
        // people and cards as the About page's section, filtered and capped.
        id: 'team',
        title: 'Trainers & mentors',
        hint: 'The people from your Team tab, on the home page too. Off until you switch it on; skipped while nobody matches who you chose to show.',
        visibility: {
          key: 'showTeam',
          optIn: true,
          hiddenNote: 'Off — your home page does not show this section. Tick “Show on website” to add it; what you enter here is kept either way.',
        },
        fields: [
          {
            key: 'teamShow',
            label: 'Who to show',
            type: 'select',
            emptyOption: 'Trainers and mentors',
            options: [
              { value: 'trainers', label: 'Trainers only' },
              { value: 'mentors', label: 'Mentors only' },
            ],
            hint: 'Someone marked “Trainer & mentor” counts as both.',
          },
          {
            key: 'teamLimit',
            label: 'How many',
            type: 'select',
            emptyOption: 'Up to 8',
            options: [
              { value: '4', label: 'Up to 4' },
              { value: '12', label: 'Up to 12' },
            ],
            hint: 'In your Team tab order. When there are more, a link to the About page shows everyone.',
          },
          { key: 'teamBadge', label: 'Badge', type: 'text', placeholder: 'Our Team' },
          {
            key: 'teamTitle',
            label: 'Heading',
            type: 'text',
            placeholder: 'Meet Our Trainers & Mentors',
            hint: 'Left blank, it follows who is shown — “Meet Our Expert Trainers”, “Meet Our Mentors” or “Meet Our Trainers & Mentors”.',
            layouts: CLASSIC,
          },
          { key: 'teamTitle', label: 'Heading', type: 'text', placeholder: 'Mentors in the weeds with you', layouts: EDITORIAL },
          { key: 'teamSubtitle', label: 'Supporting text', type: 'textarea', rows: 2 },
          { key: 'teamButtonText', label: 'Link to the About page', type: 'text', placeholder: 'Meet the whole team' },
        ],
      },
      {
        id: 'testimonials',
        title: 'Student testimonials',
        hint: 'The stories themselves are managed in the Testimonials tab; the section is skipped while there are none.',
        visibility: { key: 'showTestimonials', layouts: CLASSIC },
        fields: [
          { key: 'testimonialsBadge', label: 'Badge', type: 'text', placeholder: 'Testimonials' },
          { key: 'testimonialsTitle', label: 'Heading', type: 'text', placeholder: 'What Our Students Say' },
          { key: 'testimonialsSubtitle', label: 'Supporting text', type: 'textarea', rows: 2, layouts: CLASSIC },
          { key: 'testimonialsButtonText', label: 'Button', type: 'text', placeholder: 'Read All Stories', layouts: CLASSIC },
        ],
      },
      {
        id: 'blogs',
        title: 'Latest blog posts',
        hint: 'Your three newest published posts; the section is skipped while there are none.',
        visibility: { key: 'showBlogs', layouts: CLASSIC },
        fields: [
          { key: 'blogsBadge', label: 'Badge', type: 'text', placeholder: 'From the Blog' },
          { key: 'blogsTitle', label: 'Heading', type: 'text', placeholder: 'Latest Articles' },
          { key: 'blogsSubtitle', label: 'Supporting text', type: 'textarea', rows: 2, layouts: CLASSIC },
        ],
      },
      {
        id: 'faq',
        title: 'Frequently asked questions',
        hint: 'Also used on the FAQs page until that page has questions of its own.',
        visibility: { key: 'showFaq', layouts: CLASSIC },
        fields: [
          { key: 'faqBadge', label: 'Badge', type: 'text', placeholder: 'FAQ', layouts: CLASSIC },
          { key: 'faqSectionTitle', label: 'Heading', type: 'text', placeholder: 'Frequently Asked Questions', layouts: CLASSIC },
          { key: 'faqsTitle', label: 'Heading', type: 'text', placeholder: 'Common questions', layouts: EDITORIAL },
          { key: 'faqSubtitle', label: 'Supporting text', type: 'text', placeholder: 'Everything you need to know before you enroll.', layouts: CLASSIC },
          { key: 'faqAsideTitle', label: 'Help box — title', type: 'text', placeholder: 'Still have questions?', layouts: CLASSIC },
          { key: 'faqAsideText', label: 'Help box — text', type: 'text', placeholder: 'Our team is happy to help.', layouts: CLASSIC },
          { key: 'faqAsideCta', label: 'Help box — button', type: 'text', placeholder: 'Talk to a Counsellor', layouts: CLASSIC },
          faqRepeater('faqs', { hint: 'The section is skipped while there are no questions.' }),
        ],
      },
      {
        id: 'cta',
        title: 'Closing call to action',
        hint: 'The banner at the end of the page.',
        visibility: { key: 'showCta', layouts: CLASSIC },
        fields: [
          { key: 'ctaTitle', label: 'Heading', type: 'text', placeholder: 'Ready to Start Your Tech Journey?' },
          { key: 'ctaSubtitle', label: 'Supporting text', type: 'textarea', rows: 2 },
          { key: 'ctaPrimaryText', label: 'Main button', type: 'text', placeholder: 'Explore Courses' },
          { key: 'ctaSecondaryText', label: 'Second button', type: 'text', placeholder: 'Talk to a Counsellor' },
          {
            key: 'ctaCards',
            label: 'Highlight cards',
            type: 'repeater',
            hint: 'Optional small cards under the buttons.',
            itemLabel: 'Card',
            titleKey: 'title',
            addLabel: 'Add card',
            inline: true,
            max: 4,
            defaultItem: { icon: 'bi-trophy', title: '', subtitle: '' },
            item: [
              { key: 'icon', label: 'Icon', type: 'icon', options: BI_CTA_ICONS },
              { key: 'title', label: 'Title', type: 'text', placeholder: 'e.g. Explore Courses' },
              { key: 'subtitle', label: 'Text', type: 'text', placeholder: 'e.g. Live & self-paced' },
            ],
            layouts: CLASSIC,
          },
        ],
      },
    ],
  },

  // ── About ───────────────────────────────────────────────────────────────
  {
    pageKey: 'about',
    label: 'About',
    icon: Info,
    path: '/about',
    sections: [
      banner('About Your Institute', 'Building the next generation of tech professionals since 2018.'),
      {
        id: 'story',
        title: 'Our story',
        fields: [
          { key: 'storyBadge', label: 'Badge', type: 'text', placeholder: 'Who We Are', layouts: CLASSIC },
          { key: 'storyTitle', label: 'Heading', type: 'text', placeholder: 'Transforming Careers Through Quality Education' },
          { key: 'storyParagraph1', label: 'First paragraph', type: 'textarea', rows: 4 },
          { key: 'storyParagraph2', label: 'Second paragraph', type: 'textarea', rows: 4 },
          { key: 'storyIntroImage', label: 'Photo', type: 'image', hint: 'Shown beside the story. About 1200 × 900 px.' },
          { key: 'storyIntroImageAlt', label: 'Photo description', type: 'text', placeholder: 'e.g. Classroom with students at laptops' },
        ],
      },
      {
        id: 'timeline',
        title: 'Milestones',
        hint: 'A short timeline under the story. Hidden until you add one.',
        layouts: EDITORIAL,
        fields: [
          {
            key: 'timeline',
            label: 'Milestones',
            type: 'repeater',
            itemLabel: 'Milestone',
            titleKey: 'title',
            addLabel: 'Add milestone',
            inline: true,
            max: 8,
            defaultItem: { year: '', title: '', text: '' },
            item: [
              { key: 'year', label: 'Year', type: 'text', placeholder: '2018' },
              { key: 'title', label: 'Title', type: 'text', placeholder: 'First batch' },
              { key: 'text', label: 'Description', type: 'text' },
            ],
          },
        ],
      },
      {
        id: 'stats',
        title: 'Key numbers',
        visibility: { key: 'showStats', layouts: CLASSIC },
        fields: [statsRepeater('Until you add your own, sample figures are shown.')],
      },
      {
        id: 'mission',
        title: 'Mission & vision',
        visibility: { key: 'showMissionVision', layouts: CLASSIC },
        fields: [
          { key: 'missionTitle', label: 'Mission — heading', type: 'text', placeholder: 'Our Mission', layouts: CLASSIC },
          { key: 'missionText', label: 'Mission', type: 'textarea', rows: 3 },
          { key: 'visionTitle', label: 'Vision — heading', type: 'text', placeholder: 'Our Vision', layouts: CLASSIC },
          { key: 'visionText', label: 'Vision', type: 'textarea', rows: 3 },
        ],
      },
      {
        id: 'why-us',
        title: 'Why choose us',
        hint: 'Until you add your own cards, six samples are shown — including claims such as batch sizes and EMI options, so check them.',
        layouts: CLASSIC,
        visibility: { key: 'showWhyUs', layouts: CLASSIC },
        fields: [
          { key: 'whyUsBadge', label: 'Badge', type: 'text', placeholder: 'Why Choose Us' },
          { key: 'whyUsTitle', label: 'Heading', type: 'text', placeholder: 'The Your Institute Advantage' },
          {
            key: 'whyUs',
            label: 'Cards',
            type: 'repeater',
            itemLabel: 'Card',
            titleKey: 'title',
            addLabel: 'Add card',
            inline: true,
            columns: 2,
            max: 9,
            defaultItem: { icon: 'bi-star-fill', title: '', text: '' },
            item: [
              { key: 'icon', label: 'Icon', type: 'icon', options: BI_FEATURE_ICONS },
              { key: 'title', label: 'Title', type: 'text', placeholder: 'e.g. Small batches' },
              { key: 'text', label: 'Description', type: 'textarea', rows: 2 },
            ],
          },
        ],
      },
      {
        // Kept as `trainers` / `trainers*` keys: live sites hold copy under
        // them, and the section has only grown to include mentors. Those keys
        // word the ONE section; the two separate sections have their own
        // (`trainerSection*` / `mentorSection*`), so switching back and forth
        // loses neither. vc_cms: lib/team.aboutTeamSections.
        id: 'trainers',
        title: 'Trainers & mentors',
        hint: 'Everyone in the Team tab — trainers and mentors — plus any instructor marked “Show on website”. Skipped while there are none.',
        visibility: { key: 'showTrainersSection' },
        fields: [
          {
            key: 'teamLayout',
            label: 'How to show them',
            type: 'select',
            wide: true,
            emptyOption: 'One section — trainers and mentors together',
            options: [{ value: 'separate', label: 'Two sections — trainers, then mentors' }],
            hint: 'With two sections, someone marked “Trainer & mentor” appears in both, and a section with nobody in it is left out. Each section’s heading is set below.',
          },
          { key: 'trainersBadge', label: 'Badge', type: 'text', placeholder: 'Our Team', visibleWhen: ONE_TEAM_SECTION },
          {
            key: 'trainersTitle',
            label: 'Heading',
            type: 'text',
            placeholder: 'Meet Our Expert Trainers',
            hint: 'Left blank, it follows who is in it — “Meet Our Expert Trainers”, “Meet Our Mentors” or, with both, “Meet Our Trainers & Mentors”.',
            layouts: CLASSIC,
            visibleWhen: ONE_TEAM_SECTION,
          },
          {
            key: 'trainersTitle', label: 'Heading', type: 'text', placeholder: 'Mentors in the weeds with you', layouts: EDITORIAL, visibleWhen: ONE_TEAM_SECTION,
          },
          {
            key: 'trainersSubtitle', label: 'Supporting text', type: 'textarea', rows: 2, visibleWhen: ONE_TEAM_SECTION,
          },
        ],
      },
      {
        id: 'trainers-only',
        title: 'Trainers section',
        hint: 'Everyone marked Trainer or Trainer & mentor. Shown first.',
        visibleWhen: TWO_TEAM_SECTIONS,
        fields: [
          { key: 'trainerSectionBadge', label: 'Badge', type: 'text', placeholder: 'Our Trainers', layouts: CLASSIC },
          { key: 'trainerSectionBadge', label: 'Badge', type: 'text', placeholder: 'Trainers', layouts: EDITORIAL },
          { key: 'trainerSectionTitle', label: 'Heading', type: 'text', placeholder: 'Meet Our Expert Trainers', layouts: CLASSIC },
          { key: 'trainerSectionTitle', label: 'Heading', type: 'text', placeholder: 'Practitioners who teach every class', layouts: EDITORIAL },
          { key: 'trainerSectionSubtitle', label: 'Supporting text', type: 'textarea', rows: 2 },
        ],
      },
      {
        id: 'mentors-only',
        title: 'Mentors section',
        hint: 'Everyone marked Mentor or Trainer & mentor. Shown after the trainers.',
        visibleWhen: TWO_TEAM_SECTIONS,
        fields: [
          { key: 'mentorSectionBadge', label: 'Badge', type: 'text', placeholder: 'Our Mentors', layouts: CLASSIC },
          { key: 'mentorSectionBadge', label: 'Badge', type: 'text', placeholder: 'Mentors', layouts: EDITORIAL },
          { key: 'mentorSectionTitle', label: 'Heading', type: 'text', placeholder: 'Meet Our Mentors', layouts: CLASSIC },
          { key: 'mentorSectionTitle', label: 'Heading', type: 'text', placeholder: 'Mentors in the weeds with you', layouts: EDITORIAL },
          { key: 'mentorSectionSubtitle', label: 'Supporting text', type: 'textarea', rows: 2 },
        ],
      },
      {
        id: 'cta',
        title: 'Closing call to action',
        visibility: { key: 'showCta', layouts: CLASSIC },
        fields: [
          { key: 'ctaTitle', label: 'Heading', type: 'text', placeholder: 'Ready to Transform Your Career?' },
          {
            key: 'ctaSubtitle',
            label: 'Supporting text',
            type: 'textarea',
            rows: 2,
            hint: 'Until you write your own, the sample text claims “5000+ successful tech professionals”.',
          },
        ],
      },
    ],
  },

  // ── Courses ─────────────────────────────────────────────────────────────
  {
    pageKey: 'courses',
    label: 'Courses',
    icon: BookOpen,
    path: '/courses',
    intro: 'The course list itself comes from your published courses.',
    sections: [
      banner('Our Courses', 'Industry-relevant programs designed to make you job-ready.'),
      {
        id: 'cta',
        title: 'Counselling call to action',
        hint: 'The banner under the course list.',
        visibility: { key: 'showCta', layouts: CLASSIC },
        fields: [
          { key: 'ctaTitle', label: 'Heading', type: 'text', placeholder: 'Can’t Find What You’re Looking For?' },
          { key: 'ctaSubtitle', label: 'Supporting text', type: 'textarea', rows: 2 },
          { key: 'ctaPrimaryText', label: 'Main button', type: 'text', placeholder: 'Get Free Counseling', layouts: CLASSIC },
          { key: 'ctaSecondaryText', label: 'Second button', type: 'text', placeholder: 'Contact Us', layouts: CLASSIC },
        ],
      },
    ],
  },

  // ── Contact ─────────────────────────────────────────────────────────────
  {
    pageKey: 'contact',
    label: 'Contact',
    icon: Phone,
    path: '/contact',
    sections: [
      banner('Get in Touch', 'Have questions? We’d love to hear from you.'),
      {
        id: 'details',
        title: 'Contact details',
        hint: 'Also shown in the website footer. Blank fields fall back to Settings → General.',
        fields: [
          { key: 'phonePrimary', label: 'Phone', type: 'text', placeholder: '+91 98765 43210' },
          { key: 'phoneSecondary', label: 'Second phone', type: 'text' },
          { key: 'emailPrimary', label: 'Email', type: 'text', placeholder: 'info@example.com' },
          { key: 'emailSecondary', label: 'Second email', type: 'text' },
          { key: 'address', label: 'Address', type: 'textarea', rows: 2, placeholder: '123 Tech Park, Whitefield, Bangalore - 560066' },
          { key: 'workingHours', label: 'Working hours', type: 'textarea', rows: 2, placeholder: 'Mon - Sat: 9:00 AM - 8:00 PM', hint: 'The footer shows the first line.' },
        ],
      },
      {
        id: 'form',
        title: 'Enquiry form',
        hint: 'To change the form’s fields, switch one of your forms on for the website in Tools → Enquiry Forms.',
        fields: [
          { key: 'formTitle', label: 'Heading', type: 'text', placeholder: 'Send Us a Message' },
          { key: 'formSubtitle', label: 'Supporting text', type: 'text', placeholder: 'Our team will get back to you within 24 hours.' },
        ],
      },
      {
        id: 'map',
        title: 'Map',
        layouts: CLASSIC,
        visibility: { key: 'showMap', layouts: CLASSIC },
        fields: [
          {
            key: 'mapQuery',
            label: 'Map location',
            type: 'text',
            placeholder: 'Leave blank to use the address',
            hint: 'A place name, landmark or Google plus code — for when the address alone puts the pin in the wrong spot.',
          },
        ],
      },
      {
        id: 'cta',
        title: 'Closing call to action',
        layouts: CLASSIC,
        visibility: { key: 'showCta', layouts: CLASSIC },
        fields: [
          { key: 'ctaTitle', label: 'Heading', type: 'text', placeholder: 'Looking for Course Information?' },
          { key: 'ctaSubtitle', label: 'Supporting text', type: 'textarea', rows: 2 },
          { key: 'ctaButtonText', label: 'Button', type: 'text', placeholder: 'Explore Courses' },
        ],
      },
    ],
  },

  // ── Blogs ───────────────────────────────────────────────────────────────
  {
    pageKey: 'blogs',
    label: 'Blog',
    icon: FileText,
    path: '/blogs',
    intro: 'Posts are written in the Blogs tab.',
    sections: [
      banner('Our Blog', 'Insights, tutorials, and career advice from our expert trainers.'),
    ],
  },

  // ── FAQs ────────────────────────────────────────────────────────────────
  {
    pageKey: 'faqs',
    label: 'FAQs',
    icon: HelpCircle,
    path: '/faqs',
    sections: [
      banner('Frequently Asked Questions', 'Everything you need to know about Your Institute.'),
      {
        id: 'questions',
        title: 'Questions',
        fields: [
          faqRepeater('items', {
            category: true,
            hint: 'Leave empty to show the questions from the home page. Give questions a group to split the page into sections.',
          }),
        ],
      },
      {
        id: 'cta',
        title: 'Closing call to action',
        layouts: CLASSIC,
        visibility: { key: 'showCta', layouts: CLASSIC },
        fields: [
          { key: 'ctaTitle', label: 'Heading', type: 'text', placeholder: 'Still have questions?' },
          { key: 'ctaSubtitle', label: 'Supporting text', type: 'text', placeholder: 'Our admissions team is happy to help.' },
          { key: 'ctaButtonText', label: 'Button', type: 'text', placeholder: 'Contact Us' },
        ],
      },
    ],
  },

  // ── Gallery ─────────────────────────────────────────────────────────────
  {
    pageKey: 'gallery',
    label: 'Gallery',
    icon: Images,
    path: '/gallery',
    sections: [
      banner('Campus Gallery', 'A peek into life at Your Institute.'),
      {
        id: 'photos',
        title: 'Photos',
        hint: 'Until you add photos the page says “Gallery coming soon”. Give photos a group — Campus, Events — to show them in sections.',
        fields: [
          {
            key: 'images',
            label: 'Photos',
            type: 'repeater',
            itemLabel: 'Photo',
            titleKey: 'caption',
            addLabel: 'Add photo',
            inline: true,
            max: 60,
            defaultItem: { url: '', caption: '', category: '' },
            item: [
              { key: 'url', label: 'Photo', type: 'image', compact: true },
              { key: 'caption', label: 'Caption', type: 'text', placeholder: 'Also read by screen readers' },
              { key: 'category', label: 'Group', type: 'text', placeholder: 'e.g. Campus' },
            ],
          },
        ],
      },
      {
        id: 'cta',
        title: 'Closing call to action',
        layouts: EDITORIAL,
        fields: [
          { key: 'ctaTitle', label: 'Heading', type: 'text' },
          { key: 'ctaSubtitle', label: 'Supporting text', type: 'textarea', rows: 2 },
        ],
      },
    ],
  },

  // ── Testimonials ────────────────────────────────────────────────────────
  {
    pageKey: 'testimonials',
    label: 'Testimonials',
    icon: MessageSquareQuote,
    path: '/testimonials',
    intro: 'The stories themselves are managed in the Testimonials tab.',
    sections: [
      banner('Student Stories', 'Real stories from real students at Your Institute.'),
    ],
  },
];

export default DEFAULT_SCHEMA;
